import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

/**
 * GET /v1/review-options 강제 계약. 백엔드
 * `service/review_options.py::ReviewOptionsResp` 가 SoT 다.
 *
 * 리뷰가 부여·필터하는 어휘를 축별로 받는다. 값과 노출 순서는 서버가 소유한다.
 * 한국어 표시 문구(라벨·툴팁)와 unknown-key fallback 은 FE 가 소유한다
 * (`labelGuide.ts` / `episodeQualityTags.ts`).
 * `label-reserved-keys` 계약과 같은 DB 무관 static config 다. 그래서 세션 캐시로 1회만 받는다.
 *
 * 에피소드 리뷰에서 **결과(`task_failed`)는 여기 없다.** 결과는 어휘가 아니라 서버
 * bool 이다. 표시 문구는 FE 가 소유한다(`episodeFailure.ts`).
 */
export type ReviewOptions = {
  // 에피소드 리뷰의 결함 유형 축. 배열 순서가 칩 렌더 순서이고 순번 핫키 순번이다.
  episode_quality_tags: readonly string[];
  // 코멘트 최대 길이(code points). 입력 컨트롤이 이 값으로 초과 입력을 막는다.
  review_comment_max_length: number;
};

type ReviewOptionsResp = {
  episode_quality_tags: string[];
  review_comment_max_length: number;
};

// 엔드포인트가 실패했을 때의 degraded 응답. 어휘는 비운다(칩 없이 degrade). 하드코딩 mirror 는
// 쓰지 않는다. 최대 길이만 backend 기본값을 최후 fallback 으로 둔다. 그래야 입력이 막히지 않는다.
const DEGRADED_REVIEW_OPTIONS: ReviewOptions = {
  episode_quality_tags: [],
  review_comment_max_length: 2000,
};

// static config (DB 무관)라 세션 동안 캐시한다.
let optionsCache: ReviewOptions | null = null;
let optionsPromise: Promise<ReviewOptions> | null = null;

export function getReviewOptions(): Promise<ReviewOptions> {
  if (optionsCache !== null) {
    return Promise.resolve(optionsCache);
  }
  if (optionsPromise !== null) {
    return optionsPromise;
  }

  const promise = apiClient
    .get<ReviewOptionsResp>(apiRoutes.review.options)
    .then((response): ReviewOptions => {
      optionsCache = {
        episode_quality_tags: response.data.episode_quality_tags,
        review_comment_max_length: response.data.review_comment_max_length,
      };
      return optionsCache;
    })
    .catch(() => {
      // 실패는 캐시하지 않는다. 그래야 backend 가 복구된 뒤 다음 호출이 실제 옵션을 다시 받는다.
      return DEGRADED_REVIEW_OPTIONS;
    })
    .finally(() => {
      if (optionsPromise === promise) {
        optionsPromise = null;
      }
    });

  optionsPromise = promise;
  return promise;
}

/**
 * 계약 전체의 동기 스냅샷. 표의 행을 만들 때처럼 렌더 중 동기로 읽는 곳이 쓴다.
 *
 * 아직 받지 못했으면 null 이다. 그래서 소비처는 "계약 미수신" 과 "그 어휘에 없는 값" 을
 * 구분할 수 있다. 세션 시작 때 `AppRoot` 의 labelInputWarmup 이 prefetch 한다. 그래서 실제로
 * 쓸 때는 서버 계약 값이다. 값을 반드시 보장해야 하는 화면은 `getReviewOptions()` 를 먼저
 * await 한다.
 */
export function reviewOptionsSnapshot(): ReviewOptions | null {
  return optionsCache;
}
